import { Fragment, useEffect, useState, useCallback } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { isNil } from 'lodash/fp';
import classnames from 'classnames/bind';
import { navigateToWorkbench, getCurrentProjectSlug } from 'app/routes';
import { InspectorTabId, doSelectProjectInspectorTab } from 'app/store/preferences';
import { Button, ButtonVariant } from 'app/components/core/Button';
import { LinkButton } from 'app/components/core/LinkButton/LinkButton.jsx';
import { Icon, IconSize } from 'app/components/core/Icon';
import { Loading, LoadingSize } from 'app/components/core/Loading';
import { useToggleState, useSelector, useDispatch } from 'app/lib/hooks';
import {
    selectSnowflakeConnection,
    selectWarehouseStatus,
    doToggleWarehouseLoadDialog
} from 'app/store/healthcheck';
import {
    selectUserSessionWarehousesItemInProgress,
    selectSnowflakeRolesItemInProgress,
    selectIsProjectAndSessionEnvSame
} from 'app/store/snowflake';
import { useAccessibleTooltip } from 'app/components/core/AccessibleTooltip';
import { RoleAndWarehouseDialog } from './RoleAndWarehouseDialog.jsx';
import * as styles from './RoleAndWarehouseSelector.css';

const DEFAULT_ID = 'snowflake-role-warehouse-button';

const cx = classnames.bind(styles);

function RoleAndWarehouseButton({
    toggleDialog,
    isDialogOpen,
    showWarning,
    className,
    id,
    ref,
    ...props
}) {
    const { t } = useTranslation();
    const snowflakeStatus = useSelector(selectSnowflakeConnection);
    const currentlyLoadingWarehouse = useSelector(selectUserSessionWarehousesItemInProgress);
    const currentlyLoadingRole = useSelector(selectSnowflakeRolesItemInProgress);
    const warehouseStatus = useSelector(selectWarehouseStatus);
    const isProjectAndSessionEnvSame = useSelector(selectIsProjectAndSessionEnvSame);

    const buttonId = id ?? DEFAULT_ID;

    return (
        <Button
            className={cx('button', className)}
            variant={ButtonVariant.GHOST}
            id={buttonId}
            onClick={toggleDialog}
            aria-haspopup="true"
            aria-expanded={isDialogOpen}
            disabled={isProjectAndSessionEnvSame}
            ref={ref}
            {...props}>
            <div className={styles.roleLabel}>
                <Icon variant="role" size={IconSize.EXTRA_SMALL} inline />
                <span>
                    {currentlyLoadingRole ? (
                        <Loading size={LoadingSize.EXTRA_SMALL} />
                    ) : (
                        (snowflakeStatus?.role ?? t('common:naDashes'))
                    )}
                </span>
            </div>
            <div className={styles.warehouseLabel}>
                <Icon variant="warehouse" size={IconSize.EXTRA_SMALL} inline />
                {showWarning && <span className={styles.status} aria-live="assertive" />}
                <span>
                    {currentlyLoadingWarehouse ? (
                        <Loading size={LoadingSize.EXTRA_SMALL} />
                    ) : (
                        (warehouseStatus?.name ?? t('common:naDashes'))
                    )}
                </span>
            </div>
            <Icon variant="chevron-down" size={IconSize.EXTRA_SMALL} />
        </Button>
    );
}

function WarehouseLoadOverviewButton({ children }) {
    const dispatch = useDispatch();

    const handleClick = useCallback(() => {
        dispatch(doToggleWarehouseLoadDialog());
    }, [dispatch]);

    return <LinkButton onClick={handleClick}>{children}</LinkButton>;
}

function WarehouseLoadOverviewTooltipContent() {
    const { t } = useTranslation();
    const warehouseStatus = useSelector(selectWarehouseStatus);

    return (
        <Fragment>
            <span className={styles.tooltipKey}>
                {t('components:SnowflakeConnectionStatus.warehouseStatus.warehouseOverloaded')}
            </span>
            {['running', 'queued', 'state', 'size'].map((key) => (
                <div key={key}>
                    <span className={styles.tooltipKey}>
                        {t(`components:SnowflakeConnectionStatus.warehouseStatus.${key}`)}
                    </span>
                    <span className={styles.tooltipValue}>{warehouseStatus[key]}</span>
                </div>
            ))}
            <div className={styles.queryOverviewInfo}>
                <Trans
                    i18nKey="components:SnowflakeConnectionStatus.showQueryOverview"
                    components={{
                        linkButton: <WarehouseLoadOverviewButton />
                    }}
                />
            </div>
        </Fragment>
    );
}

function ShowProjectJobsButton({ children }) {
    const dispatch = useDispatch();

    const handleClick = useCallback(() => {
        navigateToWorkbench(getCurrentProjectSlug(), undefined, undefined, {
            toggleSidebar: 'workbench-inspector'
        });
        dispatch(doSelectProjectInspectorTab(InspectorTabId.JOBS));
    }, [dispatch]);

    return <LinkButton onClick={handleClick}>{children}</LinkButton>;
}

function DisabledSelectorTooltipContent() {
    return (
        <Trans
            i18nKey="components:SnowflakeConnectionStatus.roleWarehouseDisabled"
            components={{
                br: <br />,
                linkButton: <ShowProjectJobsButton />
            }}
        />
    );
}

export function RoleAndWarehouseSelector({ className, id }) {
    const [isDialogOpen, toggleDialog, , closeDialog] = useToggleState(false);
    const snowflakeStatus = useSelector(selectSnowflakeConnection);
    const warehouseStatus = useSelector(selectWarehouseStatus);
    const queuedCount = warehouseStatus?.queued;
    const isProjectAndSessionEnvSame = useSelector(selectIsProjectAndSessionEnvSame);
    const [showWhWarning, setShowWhWarning] = useState(queuedCount > 0);

    useEffect(() => {
        setShowWhWarning(queuedCount > 0);
    }, [queuedCount]);

    const { Tooltip, getReferenceProps, getTooltipProps } = useAccessibleTooltip({
        placement: 'bottom',
        keepTooltipOpen: true
    });

    return (
        <Fragment>
            <RoleAndWarehouseButton
                toggleDialog={toggleDialog}
                isDialogOpen={isDialogOpen}
                showWarning={showWhWarning}
                className={className}
                id={id}
                {...getReferenceProps()}
            />
            {warehouseStatus && showWhWarning && !isDialogOpen && (
                <Tooltip {...getTooltipProps()}>
                    <WarehouseLoadOverviewTooltipContent />
                </Tooltip>
            )}
            {!showWhWarning && isProjectAndSessionEnvSame && (
                <Tooltip {...getTooltipProps()}>
                    <DisabledSelectorTooltipContent />
                </Tooltip>
            )}
            {isDialogOpen && (
                <RoleAndWarehouseDialog
                    buttonId={id ?? DEFAULT_ID}
                    onClose={closeDialog}
                    hasSelectedRole={!isNil(snowflakeStatus?.role)}
                />
            )}
        </Fragment>
    );
}
